import { useState } from "react";
import { ChevronDown, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavLink } from "./NavLink";
import { navigationConfig } from "@/config/navigation";
import { Link } from "@/lib/router-compat";
import { cn } from "@/lib/utils";

interface MobileMenuItemsProps {
  onLinkClick: () => void;
}

export function MobileMenuItems({ onLinkClick }: MobileMenuItemsProps) {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryKey) ? prev.filter((key) => key !== categoryKey) : [...prev, categoryKey],
    );
  };

  const isCategoryExpanded = (categoryKey: string) => expandedCategories.includes(categoryKey);

  return (
    <div className="container mx-auto flex flex-col gap-2 pb-24">
      <NavLink to="/" onClick={onLinkClick} className="py-3 text-lg font-semibold">
        Inicio
      </NavLink>

      {Object.entries(navigationConfig).map(([categoryKey, group]) => (
        <div key={categoryKey} className="overflow-hidden rounded-xl border border-border/80 bg-card/60">
          <button
            onClick={() => toggleCategory(categoryKey)}
            className="flex w-full items-center justify-between px-4 py-3.5 text-left text-base font-semibold text-foreground"
            aria-expanded={isCategoryExpanded(categoryKey)}
          >
            <span>{group.title}</span>
            <ChevronDown
              size={18}
              className={cn(
                "shrink-0 text-muted-foreground transition-transform duration-300",
                isCategoryExpanded(categoryKey) && "rotate-180 text-seo-bright",
              )}
            />
          </button>

          <div
            className={cn(
              "overflow-hidden transition-all duration-300",
              isCategoryExpanded(categoryKey) ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0",
            )}
          >
            <ul className="space-y-0.5 border-t border-border/60 p-2">
              {group.services.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={onLinkClick}
                      className="flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-primary/10"
                    >
                      {Icon && (
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-background/60 text-primary">
                          <Icon className="h-4 w-4" aria-hidden="true" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground">{item.title}</span>
                          {item.badge && (
                            <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wider text-primary-foreground">
                              {item.badge}
                            </span>
                          )}
                        </span>
                        {item.description && (
                          <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                            {item.description}
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ))}

      <NavLink to="/blog" onClick={onLinkClick} className="py-3 text-lg font-semibold">
        Conocimiento
      </NavLink>

      <a
        href="tel:+34910626684"
        className="flex items-center gap-2 py-3 text-base font-medium text-muted-foreground"
      >
        <Phone className="h-4 w-4 text-premium-gold" aria-hidden="true" />
        910 62 66 84
      </a>

      <Button
        asChild
        className="mt-2 h-14 w-full rounded-full bg-gradient-to-r from-seo-blue to-seo-cyan text-base font-semibold shadow-lg shadow-primary/25"
        onClick={onLinkClick}
      >
        <Link to="/agenda-consultoria">
          Agenda tu diagnóstico
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Link>
      </Button>
    </div>
  );
}
